import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CITIES, getCity } from "../data/cities";
import { useSEO } from "../lib/seo";
import { Loader2, Star, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ReviewSubmit() {
  useSEO({ title: "Uw review plaatsen | Maas Kozijn", noindex: true });
  const { token } = useParams();
  const [info, setInfo] = useState(null); // {valid, used, customer_name, city_slug}
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", place: "", text: "", rating: 5 });

  useEffect(() => {
    let mounted = true;
    api.get(`/review-tokens/${token}`)
      .then((r) => { if (mounted) { setInfo(r.data); if (r.data?.customer_name) setForm((f) => ({ ...f, name: r.data.customer_name })); } })
      .catch(() => { if (mounted) setInfo({ valid: false }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cityName = info?.city_slug ? (getCity(info.city_slug)?.name || info.city_slug) : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Vul uw naam en review in.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/review-tokens/${token}/submit`, {
        name: form.name.trim(),
        place: form.place.trim(),
        text: form.text.trim(),
        rating: Number(form.rating) || 5,
      });
      setSubmitted(true);
      toast.success("Bedankt voor uw review!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Indienen mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-gradient-to-b from-slate-50 to-white py-16 md:py-24">
      <div className="container-tight max-w-2xl">
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8 md:p-12">
          {loading ? (
            <div className="flex flex-col items-center py-16 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-gold mb-3" />
              <p className="text-sm">Bezig met laden…</p>
            </div>
          ) : !info?.valid ? (
            <div className="text-center py-10" data-testid="review-link-invalid">
              <AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-3" />
              <h1 className="font-display text-2xl font-bold text-slate-900">Deze link is niet geldig</h1>
              <p className="text-slate-600 mt-2 text-sm">De link is mogelijk verlopen of incorrect. Neem gerust contact met ons op via WhatsApp of telefoon.</p>
            </div>
          ) : info.used || submitted ? (
            <div className="text-center py-10" data-testid={submitted ? "review-success" : "review-already-used"}>
              <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
              <h1 className="font-display text-2xl font-bold text-slate-900">{submitted ? "Bedankt voor uw review!" : "Deze review is al ingediend"}</h1>
              <p className="text-slate-600 mt-2 text-sm">
                {submitted
                  ? "Uw review is succesvol verstuurd en wordt binnenkort op de website geplaatst zodra hij is gecontroleerd."
                  : "Deze review-link is al gebruikt. Heeft u een nieuwe nodig? Neem gerust contact met ons op."}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Deel uw ervaring</h1>
                <p className="text-slate-600 mt-2 text-sm">
                  {cityName
                    ? `Bedankt dat u Maas Kozijn heeft gekozen voor uw project in ${cityName}. Uw review helpt andere bewoners enorm.`
                    : "Bedankt dat u Maas Kozijn heeft gekozen. Uw review helpt andere klanten enorm."}
                </p>
              </div>

              <form onSubmit={submit} className="grid gap-5" data-testid="review-form">
                <div className="grid gap-1.5">
                  <Label>Uw naam *</Label>
                  <Input data-testid="review-form-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Bijv. Familie Bakker" required />
                </div>

                <div className="grid gap-1.5">
                  <Label>Plaats / wijk</Label>
                  <Input data-testid="review-form-place" value={form.place} onChange={(e) => update("place", e.target.value)} placeholder={cityName ? `Bijv. ${cityName}-Centrum` : "Bijv. Maassluis"} />
                </div>

                <div className="grid gap-1.5">
                  <Label>Beoordeling</Label>
                  <div className="flex gap-1" data-testid="review-form-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button"
                        key={n}
                        onClick={() => update("rating", n)}
                        data-testid={`review-form-star-${n}`}
                        className="p-1 transition-transform hover:scale-110"
                        aria-label={`${n} ster${n > 1 ? "ren" : ""}`}
                      >
                        <Star className={`h-7 w-7 ${n <= form.rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <Label>Uw ervaring *</Label>
                  <Textarea
                    rows={6}
                    data-testid="review-form-text"
                    value={form.text}
                    onChange={(e) => update("text", e.target.value)}
                    placeholder="Hoe verliep de plaatsing? Wat vond u van de communicatie, het resultaat en het comfort na afloop?"
                    required
                  />
                  <p className="text-xs text-slate-400">Uw review wordt eerst gecontroleerd en daarna op de website geplaatst.</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="review-form-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold hover:bg-gold-hover text-white font-semibold py-3.5 px-6 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                  Review versturen
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          {CITIES.length > 0 ? "Maas Kozijn — Maassluis · Vlaardingen · Rotterdam en omgeving" : ""}
        </p>
      </div>
    </div>
  );
}
